import React, { useEffect } from 'react';
import { ReactComponent as ErrorTriangleIcon } from 'media/images/warning-triangle-filled.svg';
import { ReactComponent as WarningTriangleIcon } from 'media/images/warning-triangle-filled-orange.svg';
import { Modal, ModalSize } from 'components/modal/Modal';
import { ProductAmountInputFormData } from '../ProductAmountInput';
import { Product } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'rootStore';
import {
    selectBuyNowDrawerView,
    selectBuyNowErrorData,
} from 'common/features/store/duck/ui/buy-now-flow';
import { PaymentMethod } from 'common/api/e-comm/payment-types';
import { FormikProps, useFormikContext } from 'formik';
import { BuyNowDetails, getPrice, isProductOnBonus } from './BuyNowDetails';
import { useDispatch } from 'react-redux';
import { buyNowFlow } from 'common/features/store/duck/ui/buy-now-flow';
import { FadeIn } from 'components/loading-screen/FadeIn';
import { BuyNowError, Codes } from 'common/features/store/duck/ui/buy-now-flow/errors';
import { Btn } from 'components/btn/Btn';
import { BuyNowLoadingAnimation } from './BuyNowLoadingAnimation';
import { BuyNowSuccessScreen } from './BuyNowSucccess';

type BuyNowModalProps = {
    brandName: string;
    imageUrl: string;
    isOpen: boolean;
    itemDetails: ProductAmountInputFormData;
    onClose: () => void;
    onConfirmPurchase: (
        formikProps: FormikProps<ProductAmountInputFormData>,
        subtotal: number,
        fee: number,
        total: number
    ) => void;
    product: Product;
    paymentMethod?: PaymentMethod;
};

const modalSize = [ModalSize.CONTENT_WIDTH, ModalSize.MEDIUM] as [ModalSize, ModalSize];

const NoValidViewFound = () => <p>Something went wrong.</p>;

export const BuyNowModal = ({ isOpen, onClose, ...props }: BuyNowModalProps) => {
    const dispatch = useDispatch<any>();
    const formikCtx = useFormikContext();
    const view = useRootSelector(selectBuyNowDrawerView);

    const onCloseModal = () => {
        onClose();
        // Only reset form if a successful purchase happened
        if (view === 'Success') {
            formikCtx.resetForm();
        }
        // If we close we should reset the state for next use
        dispatch(buyNowFlow.resetState(null));
    };
    const price = getPrice(props.itemDetails, props.product);
    const isOnBonus = isProductOnBonus(props.product, price);

    let Content = <NoValidViewFound />;
    switch (view) {
        case 'Error': {
            Content = (
                <FadeIn when>
                    <BuyNowErrorView closeModal={onCloseModal} />
                </FadeIn>
            );
            break;
        }
        case 'Details': {
            Content = (
                <FadeIn when={true}>
                    <BuyNowDetails price={price} isOnBonus={isOnBonus} {...props} />
                </FadeIn>
            );
            break;
        }
        case 'Loading': {
            Content = <BuyNowLoadingAnimation />;
            break;
        }
        case 'Success': {
            Content = (
                <FadeIn when>
                    <BuyNowSuccessScreen imageUrl={props.imageUrl} isOnBonus={isOnBonus} />
                </FadeIn>
            );
            break;
        }
    }

    const disableClosingModal = view !== 'Loading';

    return (
        <Modal
            title=""
            escKeyCloseModalEnabled={disableClosingModal}
            showCloseButton={disableClosingModal}
            size={modalSize}
            isOpen={isOpen}
            onClose={onCloseModal}
        >
            {Content}
        </Modal>
    );
};

type BuyNowErrorViewType = {
    closeModal: BuyNowModalProps['onClose'];
};

const BuyNowErrorView = ({ closeModal }: BuyNowErrorViewType) => {
    const errorData = useRootSelector(selectBuyNowErrorData);
    const formikCtx = useFormikContext();

    const addToCart = () => {
        formikCtx.submitForm();
        closeModal();
    };

    // Case: ECOMM_0022 - unable to process payment allow user to try again
    if (errorData?.code === Codes.ECOMM_0022) {
        return (
            <BasicError errorData={errorData}>
                <div className="w-44 my-6 flex flex-col gap-3 mx-auto items-center">
                    <Btn onClick={addToCart}>Add To Cart</Btn>
                    <button
                        className="font-semibold underline text-brand"
                        disabled={formikCtx.isSubmitting}
                        onClick={closeModal}
                    >
                        Try Again
                    </button>
                </div>
            </BasicError>
        );
    }
    return (
        <BasicError errorData={errorData}>
            <Btn className="w-44 my-6" onClick={closeModal}>
                OK
            </Btn>
        </BasicError>
    );
};

interface BasicErrorProps {
    errorData: BuyNowError | null;
    children: React.ReactNode;
}
const BasicError = ({ errorData, children }: BasicErrorProps) => {
    return (
        <div className="text-center my-2 mx-1 sm:mx-7">
            {errorData?.isWarning ? (
                <WarningTriangleIcon className="mx-auto" />
            ) : (
                <ErrorTriangleIcon className="mx-auto" />
            )}
            <p className="text-22px sm:text-29px text-brand-dark font-semibold m-2 sm:m-6">
                {errorData?.title}
            </p>
            <p className="mx-0 sm:mx-5 text-grey-1 text-base">{errorData?.message}</p>
            {children}
        </div>
    );
};
